import { errorLogger } from "@/Common/helpers/logger";

// Hooks
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
// Types
import { RootState } from "@/redux/store";
// Mutation Queries
import {
	updateAllNotification as updateAllNotificationMutation,
	updateNewNotification as updateNewNotificationMutation,
	updateNotifications as updateNotificationMutation,
} from "@/Common/api/notifications/mutations";
// Handlers
import { onMarkAllAsReadHandler } from "@/Common/api/notifications/subscriptionHandlers";

export const useNotificationMutations = () => {
	const userId = useSelector((state: RootState) => state.user.user?._id);
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const queryClient = useQueryClient();

	const updateReadNotification = useCallback(
		async (id: string, read: boolean, archived: boolean) => {
			if (!locationId || !userId || !id) {
				return;
			}

			try {
				await updateNotificationMutation(locationId, id, { read, archived });
			} catch (error) {
				errorLogger(error as Error, { message: "Error running Mutation: `updateReadNotificationMutation`" });
				return;
			}
		},
		[locationId, userId]
	);

	const updateArchivedNotification = useCallback(
		async (id: string, state: { archived: boolean; read: boolean }) => {
			if (!locationId || !userId || !id) {
				return;
			}

			try {
				await updateNotificationMutation(locationId, id, state);
			} catch (error) {
				errorLogger(error as Error, { message: "Error running Mutation: `updateArchivedNotificationMutation`" });
				return;
			}
		},
		[locationId, userId]
	);

	const updateNewNotification = useCallback(async () => {
		if (!locationId || !userId) {
			return;
		}

		try {
			await updateNewNotificationMutation(locationId, userId);
		} catch (error) {
			errorLogger(error as Error, { message: "Error running Mutation: `updateNewNotificationMutation`" });
			return;
		}
	}, [locationId, userId]);

	const updateAllNotification = useCallback(async () => {
		if (!locationId || !userId) {
			return;
		}

		try {
			await updateAllNotificationMutation(locationId, userId);
			onMarkAllAsReadHandler(queryClient, userId, locationId);
		} catch (error) {
			errorLogger(error as Error, { message: "Error running Mutation: `updateAllNotification`" });
			return;
		}
	}, [locationId, userId, queryClient]);

	return {
		updateArchivedNotification,
		updateReadNotification,
		updateNewNotification,
		updateAllNotification,
	};
};
